import PropTypes from 'prop-types';
import React from 'react';
import Select, { components } from 'react-select';
import { ValidableFeedback } from './Validable';
import { allExceptIds } from '../util/arrayUtilities';
import _ from 'lodash';
import { callbackOnWidthChange } from '../util/windowUtilities';

const CustomOption = (props) => {
  const { data } = props;
  const labelStyle = data.description ? 'font-weight-bold' : null;
  const optionImage = data.imageUrl ? (
    <img className="img" src={data.imageUrl} alt="" />
  ) : null;
  return (
    <components.Option {...props}>
      <div className="custom-option media ">
        {optionImage}
        <div className="media-body">
          <div className={labelStyle}>{data.label}</div>
          {data.description && <div>{data.description}</div>}
        </div>
      </div>
    </components.Option>
  );
};

class Dropdown extends React.Component {
  constructor(props) {
    super(props);
    this.isSearchableCallback = this.isSearchableCallback.bind(this);
    this.state = {
      isSearchable: this.getIsSearchable(props.isAsync, props.forceSearchable),
      width: window.innerWidth,
    };
  }

  static propTypes = {
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.any.isRequired,
        label: PropTypes.string.isRequired,
        description: PropTypes.string,
      })
    ).isRequired,
    onChange: PropTypes.func.isRequired,
    onFocus: PropTypes.func,
    selectedId: PropTypes.any,
    className: PropTypes.string,
    showDropdownIndicator: PropTypes.bool,
    required: PropTypes.bool,
    isAsync: PropTypes.bool,
    forceSearchable: PropTypes.bool
  };

  static defaultProps = {
    showDropdownIndicator: true,
    required: false,
    isAsync: false,
    forceSearchable: false
  };

  getIsSearchable = (isAsync, forceSearchable) => {
    return forceSearchable || window.innerWidth > 760 || isAsync;
  };

  componentDidMount() {
    if (!this.props.isAsync) {
      window.addEventListener('resize', _.debounce(this.setIsSearchable, 250));
    }
  }

  componentWillUnmount() {
    if (!this.props.isAsync) {
      window.removeEventListener(
        'resize',
        _.debounce(this.setIsSearchable, 250)
      );
    }
  }

  isSearchableCallback = (newWidth) => {
    this.setState({
      width: newWidth,
      isSearchable: this.getIsSearchable(false),
    });
  };

  setIsSearchable = () => {
    callbackOnWidthChange(this.state.width, this.isSearchableCallback);
  };

  render() {
    const {
      className,
      items,
      selectedId,
      onChange,
      onFocus,
      showDropdownIndicator,
      readOnly,
      required,
      ...rest
    } = this.props;
    const value = items.find((x) => x.id === selectedId) || null;
    const selectClass = `${className}`;
    const invalid =
      required && (selectedId === null || selectedId === undefined);

    return (
      <React.Fragment>
        <Select
          isSearchable={this.state.isSearchable}
          classNamePrefix="react-select"
          className={`${selectClass} ${invalid && 'invalid'} ${
            required && invalid && 'highlighted'
          }`}
          value={value}
          onChange={(e) => onChange(e ? e.id : null)}
          onFocus={(e) => {
            if (onFocus) onFocus();
          }}
          options={items}
          isDisabled={readOnly}
          components={{
            Option: CustomOption,
            DropdownIndicator: showDropdownIndicator
              ? components.DropdownIndicator
              : null,
          }}
          noOptionsMessage={() => this.props.noOptions || 'No options'}
          {...rest}
        />
        <ValidableFeedback invalid={invalid} />
      </React.Fragment>
    );
  }
}

class AsyncDropdown extends React.Component {
  static propTypes = {
    fetchItems: PropTypes.func.isRequired,
    alwaysFetchItems: PropTypes.bool,
    alwaysOnFocus: PropTypes.bool,
    mapItemsToDropdownItems: PropTypes.func,
    initialSearchValue: PropTypes.string,
    exceptIds: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.func,
    prepopulateFromStorage: PropTypes.bool,
    selected: PropTypes.shape({
      id: PropTypes.any.isRequired,
      label: PropTypes.string.isRequired,
    }),
  };

  static defaultProps = {
    mapItemsToDropdownItems: (x) => x,
  };

  state = {
    items: [],
    currentSearchValue: '',
  };

  componentDidMount() {
    const {
      initialSearchValue,
      alwaysFetchItems,
      prepopulateFromStorage,
      selected,
    } = this.props;
    if (initialSearchValue || alwaysFetchItems) {
      this.loadItems(initialSearchValue);
    } else if (prepopulateFromStorage && selected) {
      const { items } = this.state;
      items.push(selected);
      this.setState({ items });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.selected &&
      (!this.props.selected || this.props.selected === null)
    ) {
      this.setState({ items: [] });
    }
    if (this.props.selected && prevProps.selected !== this.props.selected) {
      const { items } = this.state;
      const item = this.state.items.find(
        (x) => x.id === this.props.selected.id
      );
      if (item == null) {
        items.push(this.props.selected);
        this.setState({ items });
      }
    }
  }

  loadItems = async (searchValue) => {
    this.setState({
      isLoading: true,
    });
    const items = await this.props.fetchItems(searchValue);
    this.setState({
      items: this.props.mapItemsToDropdownItems(items),
      isLoading: false,
    });
  };

  requestLoadingItems = (searchValue) => {
    const { currentSearchValue } = this.state;
    if (currentSearchValue === null && searchValue === '') {
      return;
    }
    this.setState({ currentSearchValue: searchValue, isLoading: true });
    const timerId = window.setTimeout(() => {
      if (searchValue !== this.state.currentSearchValue) {
        return window.clearTimeout(timerId);
      }
      if (
        searchValue ||
        this.props.alwaysFetchItems ||
        this.props.alwaysOnFocus
      ) {
        this.loadItems(searchValue);
      } else {
        if (this.props.selected === null) this.setState({ items: [] });
        this.setState({ isLoading: false });
      }
    }, 300);
  };

  render() {
    const {
      fetchItems,
      alwaysFetchItems,
      mapItemsToDropdownItems,
      exceptIds,
      onChange,
      prepopulateFromStorage,
      selected,
      selectedId,
      log,
      alwaysOnFocus,
      ...rest
    } = this.props;
    const filteredItems = exceptIds
      ? allExceptIds(this.state.items, exceptIds)
      : this.state.items;

    return (
      <Dropdown
        items={filteredItems}
        isLoading={this.state.isLoading}
        onInputChange={(value) => this.requestLoadingItems(value)}
        showDropdownIndicator={false}
        filterOption={false}
        isAsync={true}
        onChange={(value) => {
          if (!value) {
            // If the value is null then the user cleared the dropdown so we reset the option list.
            this.setState({ items: [] });
          }

          onChange(
            prepopulateFromStorage && value
              ? filteredItems.find((x) => x.id === value)
              : value
          );
        }}
        onFocus={() => {
          if (
            (alwaysFetchItems || alwaysOnFocus) &&
            (!this.state.currentSearchValue ||
              this.state.currentSearchValue === null)
          ) {
            this.requestLoadingItems(null);
          }
        }}
        noOptionsMessage={() =>
          this.state.currentSearchValue
            ? 'No results found for "' + this.state.currentSearchValue + '"'
            : this.props.noOptions || 'Enter a search term...'
        }
        selectedId={selected ? selected.id : selectedId}
        {...rest}
      />
    );
  }
}

export { Dropdown, AsyncDropdown };
